import React, {useState} from 'react';
import cn from 'classnames';
import {ReactSortable} from 'react-sortablejs';
import InteractiveIcon from './InteractiveIcon';
import FormField from './FormField';
import IconListMove from '../icon/IconListMove';
import NonInteractiveIcon from './NonInteractiveIcon';
import IconLock from '../icon/IconLock';
import {SectionView} from '../../models/form/SectionView';
import {FieldView} from '../../models/form/FieldView';
import {SectionId} from '../../models/form/types';
import DeleteIcon from '../../ui/DeleteIcon';
import TicketSectionTooltip from './components/TicketSectionTooltip';
import FooterSectionTooltip from './components/FooterSectionTooltip';
import {Link} from 'react-router-dom';

type Props = {
  section: SectionView
  selectedSections: SectionView[]
  onFieldUpdate: (id: SectionId, fields: FieldView[]) => void
  onDelete: (e: React.MouseEvent<HTMLAnchorElement>, id: string) => void
  createOption: boolean
  url: string
}

export default function SortableSection({
  section,
  selectedSections,
  onFieldUpdate,
  onDelete,
  url,
  createOption,
}: Props) {
  const ticketSection = section.id === 'ticket';
  const footerSection = section.id === 'footer';
  const selectedFieldIds: string[] = selectedSections.map((x) => x.fields.map((y) => y.id)).flat(1);

  const [reordering, setReordering] = useState(false);

  const handleStateSwitch = (fieldId: string, required: boolean) => {
    const updatedFields = section.fields.map((field) => {
      if (field.id === fieldId) {
        return {...field, required};
      }
      return field;
    });
    onFieldUpdate(section.id, updatedFields);
  };

  const handleFieldDelete = (fieldId: string) => {
    const updatedFields = section.fields.filter((field) => field.id !== fieldId);
    onFieldUpdate(section.id, updatedFields);
  };

  const handleOrderChange = (reorderedFields: FieldView[]) => {
    const noNewFields = reorderedFields.filter((x) => selectedFieldIds.indexOf(x.id) < 0).length !== 0;
    if (reorderedFields.length !== section.fields.length && noNewFields) {
      const existingIds = section.fields.map((x) => x.id);
      const newElement = reorderedFields.find((x) => existingIds.indexOf(x.id) < 0);
      if (newElement) {
        // this is a hack. We have a dependence between one list and another: the change in the form template
        // affects the side form. React renders a new side form when the field is moved, but it does that
        // before React.Sortable on the side form finishes its actions. React.Sortable tries to remove the moved
        // element from the list, but the list has been already updated and not relevant anymore. Hence the exception.
        // There are two options: use a short timeout (fast but not error-proof), postpone the side form re-rendering
        // until React.Sortable on the side form finishes everything (error-proof but slow in the implementation).
        setTimeout(() => {
          onFieldUpdate(section.id, reorderedFields);
        }, 100);
      }
    } else {
      onFieldUpdate(section.id, reorderedFields);
    }
  }


  const renderFields = () => section.fields.map((field) =>
    <FormField key={field.id} field={field}
      url={url}
      onSwitchState={handleStateSwitch}
      onDelete={handleFieldDelete}
      sortable={section.sortableFields}
      hoverActive={!reordering}
      withElementManagement={createOption}/>);

  let renderedFields;
  if (section.sortableFields) {
    renderedFields = <ReactSortable list={section.fields} setList={(newState) => handleOrderChange(newState)}
      handle=".sort-handle" group="shared" animation={200} direction="vertical"
      ghostClass="block-sortable-ghost" dragClass="block-sortable-drag"
      forceFallback={true}
      onChoose={() => setReordering(true)} onUnchoose={() => setReordering(false)}>
      {renderFields()}
    </ReactSortable>;
  } else {
    renderedFields = renderFields();
  }

  return (
    <div className={cn(
      'bg-white px-4 py-5 my-4 rounded-lg p-6',
      {'border-red border-2 border-opacity-50': section.highlightError},
    )}>
      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-4 pt-1">
          {!footerSection &&
          <InteractiveIcon><IconListMove className="text-gray-dark"/></InteractiveIcon>
          }
          {footerSection &&
          <NonInteractiveIcon><IconLock className="text-gray-dark"/></NonInteractiveIcon>
          }
          <span className="font-bold">{section.name}{section.editable && <> &bull; <Link
            to={`${url}/section/${section.id}`} className="text-blue font-normal">Edit</Link></>}</span>
          {ticketSection && <TicketSectionTooltip/>}
          {footerSection && <FooterSectionTooltip/>}
        </div>
        <div className="col-span-7 -mt-2.5">
          {renderedFields}
        </div>
        <div className="col-span-1">
          {!ticketSection &&
          <DeleteIcon title="Delete section"
            onDelete={(e) => onDelete(e, section.id)}
            iconClassName="float-right mt-1"
            alwaysVisible={true}
          />
          }
        </div>
      </div>
    </div>
  );
}
